import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { navActions } from "@/app/store";
import { NAV_LINKS, PROFILE } from "@/data/portfolio";
import { NAV } from "@/constants/testIds";
import MagneticButton from "@/components/molecules/MagneticButton";
import ThemeToggle from "@/components/molecules/ThemeToggle";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const active = useSelector((s) => s.nav.active);
  const dispatch = useDispatch();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (!sections.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) dispatch(navActions.setActive(e.target.id));
        });
      },
      { threshold: 0.35 }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [dispatch]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.nav
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-[color:var(--surface-glass,rgba(21,22,28,0.7))] border-b border-[color:var(--border)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <button
          data-testid={NAV.brand}
          onClick={() => scrollTo("home")}
          className="flex items-center gap-2.5 group"
          aria-label="Home"
        >
          <span className="w-9 h-9 rounded-xl bg-[color:var(--accent)] text-white font-display flex items-center justify-center text-base">
            ST
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-sm tracking-tight text-[color:var(--text)]">
              {PROFILE.name}
            </span>
            <span className="label-mono text-[9px] tracking-[0.25em]">
              Senior Frontend Engineer
            </span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-1 px-1.5 py-1.5 rounded-full border border-[color:var(--border-strong)] bg-[color:var(--surface)]/70 backdrop-blur-md">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={NAV.link(link.id)}
              onClick={() => scrollTo(link.id)}
              className={`relative px-3.5 py-1.5 text-xs font-mono uppercase tracking-wider rounded-full transition-colors ${
                active === link.id
                  ? "text-[color:var(--text)]"
                  : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
              }`}
            >
              {active === link.id && (
                <motion.span
                  layoutId="active-pill"
                  className="absolute inset-0 rounded-full bg-[color:var(--surface-2)] border border-[color:var(--border-strong)]"
                  transition={{ type: "spring", damping: 22, stiffness: 220 }}
                />
              )}
              <span className="relative">{link.label}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button
            data-testid={NAV.commandBtn}
            onClick={() => dispatch(navActions.openCommand())}
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[color:var(--border-strong)] text-xs font-mono text-[color:var(--text-muted)] hover:text-[color:var(--text)] hover:border-[color:var(--accent)] transition"
            aria-label="Open command palette"
          >
            <kbd className="font-mono">⌘</kbd>
            <kbd className="font-mono">K</kbd>
          </button>
          <MagneticButton
            as="a"
            href={PROFILE.resumeUrl}
            data-testid={NAV.resumeBtn}
            className="bg-[color:var(--accent)] text-white hover:opacity-90 text-sm px-5 py-2"
            strength={8}
          >
            Resume
          </MagneticButton>
        </div>
      </div>
    </motion.nav>
  );
}
